import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Handshake, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, testimonials } from '../mockData';
import usePageMeta from '../hooks/usePageMeta';

const Partners = () => {
  usePageMeta({
    title: 'Trade Partners — Installation Delivery Partner | Shore Doors Locksmiths',
    description:
      'Shore Doors is the installation delivery partner access control companies and builders use across Auckland — hardware installs, cabling, commissioning and handover done properly.',
  });

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gray-950 py-16 sm:py-20 text-white overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src="/images/projects/pukekohe-train-station.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 from-25% via-gray-950/75 via-55% to-gray-950/5"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-5">
              <Handshake size={16} />
              For access control companies, builders & FM teams
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-5">Your installation delivery partner</h1>
            <p className="text-xl text-gray-300 mb-8">
              We install the hardware, you keep the client. Shore Doors has delivered lock and
              access hardware installations for Auckland's security integrators and builders for
              years — reliably, to spec, and to programme.
            </p>
            <a href={businessInfo.enquiryMailto}>
              <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-lg px-8">
                Talk to us about capacity
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What we take off your plate */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-5">What we take off your plate</h2>
            <ul className="space-y-3">
              {[
                'Physical installation of locks, maglocks, electric strikes, drop bolts and wireless locking hardware (e.g. Aperio)',
                'Low-voltage cabling and power with battery backup — terminated and labelled',
                'Door automation: swing operators, sliding doors, gates',
                'Commissioning support, testing of every access point, as-built handover docs',
                'Overflow capacity when your own installers are stretched',
                'Maintenance and remedial visits under your banner or ours',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={18} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <Card className="border-gray-200 h-fit">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">How engagement works</h3>
              <ol className="space-y-4 text-gray-700">
                <li className="flex gap-3"><span className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">1</span><span>You send the spec and programme — we price the installation scope.</span></li>
                <li className="flex gap-3"><span className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">2</span><span>We deliver to your programme, coordinating with other trades on site.</span></li>
                <li className="flex gap-3"><span className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">3</span><span>Testing, commissioning support and handover documentation close the job out.</span></li>
              </ol>
              <p className="text-sm text-gray-600 mt-6 border-t border-gray-100 pt-4">
                We're an installation partner, not a competitor — we don't design, own or manage
                access control systems, and your client relationship stays yours.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Partner proof */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            What our partners say
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {testimonials.slice(0, 4).map((t) => (
              <Card key={t.id} className="border-gray-200 bg-white">
                <CardContent className="p-7">
                  <div className={`${t.logoBgDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg px-4 py-3 inline-flex items-center justify-center mb-4`} style={{ minWidth: '150px', height: '64px' }}>
                    <img src={t.logo} alt={`${t.company} logo`} className="max-h-10 w-auto object-contain" style={{ maxWidth: '130px' }} />
                  </div>
                  <Quote className="text-blue-200 mb-2" size={22} />
                  <blockquote className="text-gray-700 leading-relaxed mb-4 italic text-sm">"{t.quote}"</blockquote>
                  <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                  <p className="text-gray-500 text-xs">{t.title}, {t.company}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/reviews">
              <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8">
                All testimonials <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Need an installation partner?</h2>
          <p className="text-lg text-gray-300 mb-7">
            Email {businessInfo.email} or call {businessInfo.phone} — we'll talk scope, coverage and programme.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={businessInfo.enquiryMailto}>
              <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold">
                Email us
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </a>
            <Link to="/projects">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold">
                See our delivery record
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;
